import { useState } from 'react'

import { Box, BoxList, Button, Text } from '@factorialco/deprecated-design-system'
import { AtsFileFragment, AtsJobPostingDocument } from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDeleteAtsFile } from 'modules/ats/hooks/files/useDeleteAtsFile'

interface Props {
  atsFile: AtsFileFragment
}

const uploadedByTo = (author?: string | null, job_posting_title?: string | null) => {
  const hasJobTitle = !!job_posting_title
  const hasAuthor = !!author

  if (hasJobTitle && hasAuthor) {
    return i18n.t('ats.candidate_details.files.uploaded_by_to', { author, job_posting_title })
  }

  if (hasAuthor) {
    return i18n.t('ats.candidate_details.files.uploaded_by', { author })
  }

  if (hasJobTitle) {
    return i18n.t('ats.candidate_details.files.uploaded_to', { job_posting_title })
  }

  return ''
}

export const OtherFilesItem = ({ atsFile }: Props) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { createdAt, file, atsJobPostingId, authorFullName, atsApplicationId } = atsFile
  const { data } = useQuery(AtsJobPostingDocument, {
    variables: { id: String(atsJobPostingId) },
    skip: !atsJobPostingId,
  })
  const { deleteAtsFile, loading: deletingFile } = useDeleteAtsFile(atsFile)

  const jobPostingTitle = atsJobPostingId ? data?.ats.jobPosting?.title : null
  const uploadedAt = moment(createdAt).format('ll')

  const { navigation } = useNavigation()

  const onDelete = async () => {
    const handled = handleResult(await deleteAtsFile(), () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.removed_failed'),
      })
    }
  }

  return (
    <BoxList.Item>
      <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
        {file.contentType && <DocumentType mimetype={file.contentType} />}
        <Box overflowX="hidden">
          <Text.Normal weight="medium" ellipsis overflowWrap>
            {file.filename}
          </Text.Normal>
          <Box paddingTop="s4">
            <Text.Normal size="200" color="grey600">
              {uploadedAt} - {uploadedByTo(authorFullName, jobPostingTitle)}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
      <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
        <Box flexDirection="row" gap="s8">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative
                type="secondary"
                aria-label={i18n.t('ats.candidate_details.more_actions')}
                iconName="ellipsis"
              />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="end">
              {file.url && (
                <PopoverMenu.HrefOption
                  href={file.url}
                  download={file.filename}
                  target="_blank"
                  title={i18n.t('documents.download')}
                />
              )}
              <PopoverMenu.TouchableOption
                onClick={() => setIsDeleteModalOpen(true)}
                title={i18n.t('documents.delete')}
                danger
              />
            </PopoverMenu.Content>
          </PopoverMenu>

          {isDeleteModalOpen && !deletingFile && (
            <Modal
              title={i18n.t('documents.confirm_delete.title')}
              description={i18n.t('documents.confirm_delete.description')}
              illustration="input-field"
              onClose={() => setIsDeleteModalOpen(false)}
              onSubmit={onDelete}
              footer={
                <Button type="negative" disabled={deletingFile} submit>
                  {i18n.t('documents.delete')}
                </Button>
              }
            />
          )}
        </Box>

        {atsJobPostingId && (
          <Button.Router
            type="secondary"
            disabled={!atsApplicationId}
            aria-label={i18n.t('ats.candidates.candidate_application', {
              job_application_name: jobPostingTitle,
            })}
            to={
              !!atsApplicationId && !!atsJobPostingId
                ? navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                    jobId: atsJobPostingId,
                    application_id: atsApplicationId,
                  })
                : '#'
            }
            iconName="arrow-right"
          />
        )}
      </Box>
    </BoxList.Item>
  )
}
